import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Box,
  Text,
  Divider,
  Select,
  Flex,
  HStack,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  Switch
} from 'components-lib';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { MdRemoveCircle } from 'react-icons/md';
import { axiosInstance } from 'config/api';
import { API_ENDPOINTS } from '../../config/api';

const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    id: "",
    doctor: "",
    slot_duration: "",
    opd_timing: {},
    emergency_charge: "",
    payment_details: {
      account_holder: "",
      account_number: "",
      bank_name: "",
      ifsc_code: "",
      upi_id: "",
    },
    language: "",
    whatsapp_no: "",
    medical_license: "",
    qualification: "",
    services: [{ serviceName: "", unitPrice: "", discount: "" }],
    clinic_id: "",
    selectedWeekdays: [],
    timingStart: "",
    timingEnd: "",
    vacation: {
      isOnVacation: false,
      fromDate: "",
      toDate: ""
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalDoctorData, setOriginalDoctorData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();
  const [clinicOptions, setClinicOptions] = useState([]);

  // Fetch current doctor data (stable)
  const fetchDoctorData = useCallback(async (doctorId) => {
    try {
      const response = await axiosInstance.get(`/api/doctor/profile?doctor_id=${doctorId}`);

      if (!response.data) {
        throw new Error('No data received from profile endpoint');
      }

      const doctor = response.data;

      // Store original doctor data for preserving unchanged fields
      setOriginalDoctorData(doctor);

      // Extract doctor fields from nested structure if needed
      const doctorFields = doctor.doctor || doctor;

      // Parse OPD timing from string format to object
      let opdTimings = {};
      try {
        const opdTimingSource = doctorFields.opd_timing || doctor.opdTiming;
        if (typeof opdTimingSource === 'string') {
          opdTimings = JSON.parse(opdTimingSource);
        } else {
          opdTimings = opdTimingSource || {};
        }
      } catch (error) {
        console.error('Error parsing OPD timing:', error);
        opdTimings = {};
      }

      // Get selected weekdays from OPD timing
      const selectedDays = Object.entries(opdTimings)
        .filter(([_, timing]) => timing && timing !== "null" && timing !== "")
        .map(([day]) => day);

      // Handle payment details - support both old and new format
      let paymentDetails = {
        account_holder: "",
        account_number: "",
        bank_name: "",
        ifsc_code: "",
        upi_id: "",
      };

      if (doctorFields.payment_details && typeof doctorFields.payment_details === 'object') {
        // New structured format
        paymentDetails = {
          account_holder: doctorFields.payment_details.account_holder || "",
          account_number: doctorFields.payment_details.account_number || "",
          bank_name: doctorFields.payment_details.bank_name || "",
          ifsc_code: doctorFields.payment_details.ifsc_code || "",
          upi_id: doctorFields.payment_details.upi_id || "",
        };
      } else {
        // Old format - convert from upi_details and bank_details strings
        if (doctorFields.upi_details) {
          paymentDetails.upi_id = doctorFields.upi_details;
        }
        if (doctorFields.bank_details) {
          // Parse the old bank_details string to extract individual fields
          // Format: "MH BANK, A/C: 996665558896, IFSC: SBIN0000454"
          const bankDetailsStr = doctorFields.bank_details;

          // Extract bank name (everything before the first comma)
          const bankNameMatch = bankDetailsStr.match(/^([^,]+)/);
          if (bankNameMatch) {
            paymentDetails.bank_name = bankNameMatch[1].trim();
          }

          // Extract account number (A/C: followed by numbers)
          const accountMatch = bankDetailsStr.match(/A\/C:\s*([^,]+)/);
          if (accountMatch) {
            paymentDetails.account_number = accountMatch[1].trim();
          }

          // Extract IFSC code (IFSC: followed by alphanumeric)
          const ifscMatch = bankDetailsStr.match(/IFSC:\s*([^,]+)/);
          if (ifscMatch) {
            paymentDetails.ifsc_code = ifscMatch[1].trim();
          }

          // Extract account holder (Holder: followed by name)
          const holderMatch = bankDetailsStr.match(/Holder:\s*([^,]+)/);
          if (holderMatch) {
            paymentDetails.account_holder = holderMatch[1].trim();
          }
        }
      }

      // Set form data
      setFormData({
        id: doctorFields.id || doctor.id,
        doctor: doctorFields.doctor || doctor.doctorName || "",
        slot_duration: doctorFields.slot_duration || "",
        opd_timing: opdTimings,
        emergency_charge: doctorFields.emergency_charge || doctor.emergencyCharge || "",
        payment_details: paymentDetails,
        language: doctorFields.language || doctor.languages || "",
        whatsapp_no: doctorFields.whatsapp_no || "",
        medical_license: doctorFields.medical_license || "",
        qualification: doctorFields.qualification || "",
        services: doctorFields.services || [{ serviceName: "", unitPrice: "", discount: "" }],
        clinic_id: doctorFields.clinic_id || "",
        selectedWeekdays: selectedDays,
        timingStart: "",
        timingEnd: "",
        vacation: {
          isOnVacation: false,
          fromDate: "",
          toDate: ""
        }
      });
    } catch (error) {
      console.error('Error fetching doctor data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load doctor data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const response = await axiosInstance.get('/api/clinic/allClinics');
        console.log('Clinics response:', response.data);

        let clinics = response.data;

        // Handle different response formats
        if (Array.isArray(clinics)) {
          // Direct array format
          clinics = clinics.map(clinic => ({
            id: clinic.id || clinic._id || clinic.clinic_id,
            name: clinic.name || clinic.clinic_name
          }));
        } else if (clinics && clinics.clinics) {
          // Nested format
          clinics = clinics.clinics.map(clinic => ({
            id: clinic.id || clinic._id || clinic.clinic_id,
            name: clinic.name || clinic.clinic_name
          }));
        } else if (clinics && typeof clinics === 'object') {
          // Single clinic object
          clinics = [{
            id: clinics.id || clinics._id || clinics.clinic_id,
            name: clinics.name || clinics.clinic_name
          }];
        } else {
          // Fallback to empty array
          clinics = [];
        }

        console.log('Processed clinics:', clinics);
        setClinicOptions(clinics);
      } catch (error) {
        console.error('Error fetching clinics:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setClinicOptions([]); // Set empty array on error
        toast({
          title: "Error",
          description: "Failed to fetch clinics",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    if (isOpen) {
      fetchClinics();

      // Only load doctor data for doctors
      const tokenPayload = JSON.parse(localStorage.getItem('tokenPayload') || '{}');
      if (tokenPayload.role === 'doctor') {
        fetchDoctorData(tokenPayload.userId);
      }
    }
  }, [isOpen, toast, fetchDoctorData]);

  // (Removed duplicate fetchDoctorData definition here)

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      payment_details: {
        ...prev.payment_details,
        [name]: value
      }
    }));
  };

  const handleServiceChange = (index, field, value) => {
    const updatedServices = formData.services.map((service, i) =>
      i === index ? { ...service, [field]: value } : service
    );
    setFormData(prev => ({ ...prev, services: updatedServices }));
  };

  const addService = () => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { serviceName: "", unitPrice: "", discount: "" }]
    }));
  };

  const removeService = (index) => {
    if (formData.services.length > 1) {
      setFormData(prev => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index)
      }));
    }
  };

  const handleWeekdayToggle = (day) => {
    setFormData(prev => {
      const selected = prev.selectedWeekdays ? [...prev.selectedWeekdays] : [];
      const index = selected.indexOf(day);
      if (index > -1) {
        selected.splice(index, 1);
        // Clear timing for this day
        const newOpd = { ...prev.opd_timing };
        newOpd[day.toLowerCase()] = null;
        return { ...prev, selectedWeekdays: selected, opd_timing: newOpd };
      } else {
        selected.push(day);
        return { ...prev, selectedWeekdays: selected };
      }
    });
  };

  const handleSetTiming = () => {
    const { timingStart, timingEnd, selectedWeekdays, opd_timing } = formData;
    if (!timingStart || !timingEnd || !selectedWeekdays || selectedWeekdays.length === 0) {
      return;
    }
    const newTimings = { ...opd_timing };
    selectedWeekdays.forEach(day => {
      newTimings[day.toLowerCase()] = `${timingStart}-${timingEnd}`;
    });
    setFormData(prev => ({
      ...prev,
      opd_timing: newTimings,
      timingStart: "",
      timingEnd: "",
      selectedWeekdays: []
    }));
  };

  // Vacation handlers (UI only)
  const handleVacationToggle = () => {
    setFormData(prev => {
      const currentVacation = prev.vacation || { isOnVacation: false, fromDate: "", toDate: "" };
      return {
        ...prev,
        vacation: {
          ...currentVacation,
          isOnVacation: !currentVacation.isOnVacation,
          fromDate: !currentVacation.isOnVacation ? currentVacation.fromDate : "",
          toDate: !currentVacation.isOnVacation ? currentVacation.toDate : ""
        }
      };
    });
  };

  const handleVacationDateChange = (field, value) => {
    setFormData(prev => {
      const currentVacation = prev.vacation || { isOnVacation: false, fromDate: "", toDate: "" };
      return {
        ...prev,
        vacation: {
          ...currentVacation,
          [field]: value
        }
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Only validate if the field has been changed from original or is truly empty
    if (!formData.doctor && (!originalDoctorData || !originalDoctorData.doctor)) {
      newErrors.doctor = "Doctor name is required";
    }

    // For edit forms, we're more lenient - only validate if user is clearly trying to set a value
    // Most fields are optional in edit mode since we preserve original data

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const tokenPayload = JSON.parse(localStorage.getItem('tokenPayload') || '{}');

      if (tokenPayload.role === 'doctor') {
        // Convert structured payment_details back to old format for API
        const { payment_details } = formData;
        let upi_details = '';
        let bank_details = '';

        if (payment_details) {
          // Build UPI details string
          if (payment_details.upi_id) {
            upi_details = payment_details.upi_id;
          }

          // Build bank details string
          const bankParts = [];
          if (payment_details.bank_name) bankParts.push(payment_details.bank_name);
          if (payment_details.account_number) bankParts.push(`A/C: ${payment_details.account_number}`);
          if (payment_details.ifsc_code) bankParts.push(`IFSC: ${payment_details.ifsc_code}`);
          if (payment_details.account_holder) bankParts.push(`Holder: ${payment_details.account_holder}`);

          bank_details = bankParts.join(', ');
        }

        // Extract only the doctor fields from original data, not the entire nested object
        const originalDoctorFields = originalDoctorData?.doctor || originalDoctorData || {};

        // Handle services array - use form data if it has valid services, otherwise preserve original
        let servicesArray = formData.services;
        if (!servicesArray || servicesArray.length === 0 || (servicesArray.length === 1 && !servicesArray[0].serviceName)) {
          // If no valid services in form, try to preserve original services
          if (originalDoctorFields.services && Array.isArray(originalDoctorFields.services)) {
            servicesArray = originalDoctorFields.services;
          } else {
            // Fallback to empty array
            servicesArray = [];
          }
        }

        // Build the update data with proper field mapping
        const updateData = {
          id: formData.id || originalDoctorFields.id,
          doctor: formData.doctor || originalDoctorFields.doctor,
          slot_duration: formData.slot_duration || originalDoctorFields.slot_duration,
          opd_timing: formData.opd_timing || originalDoctorFields.opd_timing,
          emergency_charge: formData.emergency_charge || originalDoctorFields.emergency_charge,
          upi_details: upi_details || originalDoctorFields.upi_details,
          bank_details: bank_details || originalDoctorFields.bank_details,
          language: formData.language || originalDoctorFields.language,
          whatsapp_no: formData.whatsapp_no || originalDoctorFields.whatsapp_no,
          medical_license: formData.medical_license || originalDoctorFields.medical_license,
          qualification: formData.qualification || originalDoctorFields.qualification,
          services: servicesArray,
          clinic_id: formData.clinic_id || originalDoctorFields.clinic_id
        };

        console.log('=== PROFILE SETTINGS API CALL BODY ===');
        console.log(JSON.stringify(updateData, null, 2));
        console.log('=== END API CALL BODY ===');

        const response = await axiosInstance.post(API_ENDPOINTS.DOCTOR.UPDATE, updateData);

        if (response.data) {
          toast({
            title: "Success",
            description: "Profile updated successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
        }
      } else {
        // For other users, use existing logic
        const userData = JSON.parse(localStorage.getItem('user'));
        const payload = {
          email: formData.email,
          whatsapp_no: formData.whatsapp_no
        };

        // Only include password if it was changed
        if (formData.password) {
          payload.password = formData.password;
        }

        const response = await axiosInstance.put(`/api/frontdesk/update/${userData.id}`, payload);

        if (response.data) {
          // Update local storage with new data
          const updatedUser = { ...userData, ...payload };
          delete updatedUser.password; // Remove password from stored data
          localStorage.setItem('user', JSON.stringify(updatedUser));

          toast({
            title: 'Profile Updated',
            description: 'Your profile has been successfully updated.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onClose();
        }
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update profile",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tokenPayload = JSON.parse(localStorage.getItem('tokenPayload') || '{}');
  const isDoctor = tokenPayload.role === 'doctor';

  if (!formData) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader>Profile Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isDoctor ? (
            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                {/* Basic Information */}
                <Text fontSize="lg" fontWeight="semibold">Basic Information</Text>

                <FormControl>
                  <FormLabel>Clinic</FormLabel>
                  <Select
                    name="clinic_id"
                    value={formData.clinic_id}
                    onChange={handleChange}
                    placeholder="Select Clinic"
                  >
                    {clinicOptions.map(clinic => (
                      <option key={clinic.id} value={clinic.id}>
                        {clinic.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <HStack spacing={4}>
                  <FormControl isInvalid={!!errors.doctor}>
                    <FormLabel>Doctor Name</FormLabel>
                    <Input
                      name="doctor"
                      value={formData.doctor}
                      onChange={handleChange}
                      placeholder="Enter doctor name"
                    />
                    <FormErrorMessage>{errors.doctor}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.slot_duration}>
                    <FormLabel>Slot Duration (minutes)</FormLabel>
                    <Input
                      name="slot_duration"
                      value={formData.slot_duration}
                      onChange={handleChange}
                      placeholder="20"
                      type="text"
                    />
                    <FormErrorMessage>{errors.slot_duration}</FormErrorMessage>
                  </FormControl>
                </HStack>

                <HStack spacing={4}>
                  <FormControl isInvalid={!!errors.emergency_charge}>
                    <FormLabel>Emergency Charge</FormLabel>
                    <Input
                      name="emergency_charge"
                      value={formData.emergency_charge}
                      onChange={handleChange}
                      placeholder="600"
                      type="text"
                    />
                    <FormErrorMessage>{errors.emergency_charge}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.whatsapp_no}>
                    <FormLabel>WhatsApp Number</FormLabel>
                    <Input
                      name="whatsapp_no"
                      value={formData.whatsapp_no}
                      onChange={handleChange}
                      placeholder="+919999998888"
                    />
                    <FormErrorMessage>{errors.whatsapp_no}</FormErrorMessage>
                  </FormControl>
                </HStack>

                <HStack spacing={4}>
                  <FormControl isInvalid={!!errors.medical_license}>
                    <FormLabel>Medical License</FormLabel>
                    <Input
                      name="medical_license"
                      value={formData.medical_license}
                      onChange={handleChange}
                      placeholder="MED7654321IN"
                    />
                    <FormErrorMessage>{errors.medical_license}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.qualification}>
                    <FormLabel>Qualification</FormLabel>
                    <Input
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleChange}
                      placeholder="MBBS, DNB (Cardiology)"
                    />
                    <FormErrorMessage>{errors.qualification}</FormErrorMessage>
                  </FormControl>
                </HStack>

                <FormControl>
                  <FormLabel>Languages</FormLabel>
                  <Input
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    placeholder="English, Hindi, Spanish"
                  />
                </FormControl>

                  <Flex gap={4}>
                    <Box flex="1">
                      <FormControl>
                        <FormLabel>Password (optional)</FormLabel>
                        <InputGroup>
                          <Input
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter new password"
                            type={showPassword ? "text" : "password"}
                          />
                          <InputRightElement>
                            <IconButton
                              variant="ghost"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                              onClick={() => setShowPassword(!showPassword)}
                            />
                          </InputRightElement>
                        </InputGroup>
                      </FormControl>
                    </Box>
                    <Box flex="1">
                      <FormControl>
                        <FormLabel>WhatsApp Number</FormLabel>
                        <Input
                          name="whatsapp_no"
                          value={formData.whatsapp_no}
                          onChange={handleChange}
                          type="tel"
                        />
                      </FormControl>
                    </Box>
                  </Flex>

                  <Flex gap={4}>
                    <Box flex="1">
                      <FormControl>
                        <FormLabel>Slot Duration (minutes)</FormLabel>
                        <NumberInput value={formData.slot_duration}>
                          <NumberInputField
                            onChange={(e) =>
                              setFormData(prev => ({ ...prev, slot_duration: parseInt(e.target.value) || 0 }))
                            }
                          />
                        </NumberInput>
                      </FormControl>
                    </Box>
                    <Box flex="1">
                      <FormControl>
                        <FormLabel>Emergency Charge</FormLabel>
                        <NumberInput value={formData.emergency_charge}>
                          <NumberInputField
                            onChange={(e) =>
                              setFormData(prev => ({ ...prev, emergency_charge: parseInt(e.target.value) || 0 }))
                            }
                          />
                        </NumberInput>
                      </FormControl>
                    </Box>
                  </Flex>

                  {/* OPD Timing */}
                  <Divider my={6} />
                  <Box>
                    <Text fontSize="lg" fontWeight="semibold" mb={4}>Set OPD Timings for Weekdays</Text>
                    <FormControl>
                      <FormLabel>Select Weekdays</FormLabel>
                      <Flex wrap="wrap" gap={3} mb={2}>
                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                          <Box key={day} display="flex" alignItems="center" gap={1} width="120px">
                            <input
                              type="checkbox"
                              id={`weekday-checkbox-${day}`}
                              checked={formData.selectedWeekdays?.includes(day)}
                              onChange={() => handleWeekdayToggle(day)}
                            />
                            <label htmlFor={`weekday-checkbox-${day}`}>{day}</label>
                          </Box>
                        ))}
                      </Flex>
                      <Flex gap={2} align="center" mb={3}>
                        <Input
                          type="time"
                          name="timingStart"
                          value={formData.timingStart}
                          onChange={(e) => setFormData(prev => ({ ...prev, timingStart: e.target.value }))}
                          placeholder="Start Time"
                          width="120px"
                        />
                        <Text>to</Text>
                        <Input
                          type="time"
                          name="timingEnd"
                          value={formData.timingEnd}
                          onChange={(e) => setFormData(prev => ({ ...prev, timingEnd: e.target.value }))}
                          placeholder="End Time"
                          width="120px"
                        />
                        <Button
                          size="sm"
                          colorScheme="blue"
                          onClick={handleSetTiming}
                        >
                          Set Timing for Selected Days
                        </Button>
                      </Flex>
                      {Object.entries(formData.opd_timing || {})
                        .filter(([_, timing]) => timing && timing !== "null")
                        .map(([day, timing]) => (
                          <Box key={day} mb={3} borderWidth="1px" borderRadius="md" p={3}>
                            <Text fontWeight="bold" mb={2} textTransform="capitalize">{day}</Text>
                            <Flex gap={2} align="center">
                              <Text fontSize="sm">{timing}</Text>
                              <IconButton
                                aria-label="Remove timing"
                                icon={<MdRemoveCircle color="red" />}
                                size="sm"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    opd_timing: {
                                      ...prev.opd_timing,
                                      [day]: null
                                    }
                                  }));
                                }}
                              />
                            </Flex>
                          </Box>
                        ))}
                    </FormControl>
                  </Box>

                  <Flex gap={4}>
                    <Box flex="1">
                      <FormControl isInvalid={!!errors.medical_license}>
                        <FormLabel>Medical License</FormLabel>
                        <Input
                          name="medical_license"
                          value={formData.medical_license}
                          onChange={handleChange}
                        />
                        <FormErrorMessage>{errors.medical_license}</FormErrorMessage>
                      </FormControl>
                    </Box>
                    <Box flex="1">
                      <FormControl isInvalid={!!errors.qualification}>
                        <FormLabel>Qualification</FormLabel>
                        <Input
                          name="qualification"
                          value={formData.qualification}
                          onChange={handleChange}
                        />
                        <FormErrorMessage>{errors.qualification}</FormErrorMessage>
                      </FormControl>
                    </Box>
                  </Flex>



                  <Divider my={4} />
                  <Text fontSize="lg" fontWeight="semibold">Services</Text>
                  {formData.services.map((service, index) => (
                    <Flex key={index} gap={4} align="center">
                      <Box flex="1">
                        <FormLabel>Service Name</FormLabel>
                        <Input
                          value={service.serviceName}
                          onChange={(e) =>
                            handleServiceChange(index, "serviceName", e.target.value)
                          }
                        />
                      </Box>
                      <Box flex="1">
                        <FormLabel>Unit Price</FormLabel>
                        <NumberInput value={service.unitPrice}>
                          <NumberInputField
                            onChange={(e) =>
                              handleServiceChange(
                                index,
                                "unitPrice",
                                parseFloat(e.target.value)
                              )
                            }
                          />
                        </NumberInput>
                      </Box>
                      <Box flex="1">
                        <FormLabel>Discount</FormLabel>
                        <NumberInput value={service.discount}>
                          <NumberInputField
                            onChange={(e) =>
                              handleServiceChange(
                                index,
                                "discount",
                                parseFloat(e.target.value)
                              )
                            }
                          />
                        </NumberInput>
                      </Box>
                      <IconButton
                        icon={<MdRemoveCircle color="red" />}
                        onClick={() => removeService(index)}
                        border="none"
                        colorScheme="white"
                        aria-label="Remove service"
                        mt={6}
                      />
                    </Flex>
                  ))}
                  <Button size="sm" onClick={addService}>
                    + Add Service
                  </Button>

                  <Divider my={6} />
                  <Text fontSize="lg" fontWeight="semibold">Vacation Settings</Text>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel htmlFor="vacation-toggle" mb="0">
                      On Vacation
                    </FormLabel>
                    <Switch
                      id="vacation-toggle"
                      isChecked={formData.vacation?.isOnVacation || false}
                      onChange={handleVacationToggle}
                      colorScheme="blue"
                    />
                  </FormControl>

                  {formData.vacation?.isOnVacation && (
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel>From Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.vacation?.fromDate || ""}
                          onChange={(e) => handleVacationDateChange('fromDate', e.target.value)}
                          placeholder="Select start date"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>To Date</FormLabel>
                        <Input
                          type="date"
                          value={formData.vacation?.toDate || ""}
                          onChange={(e) => handleVacationDateChange('toDate', e.target.value)}
                          placeholder="Select end date"
                        />
                      </FormControl>
                    </HStack>
                  )}

                  <Divider my={6} />
                  <Text fontSize="lg" fontWeight="semibold">Payment Details</Text>
                  <HStack spacing={4}>
                    <FormControl>
                      <FormLabel>Account Holder Name</FormLabel>
                      <Input
                        name="account_holder"
                        value={formData.payment_details.account_holder}
                        onChange={handlePaymentChange}
                        placeholder="Enter account holder name"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Account Number</FormLabel>
                      <Input
                        name="account_number"
                        value={formData.payment_details.account_number}
                        onChange={handlePaymentChange}
                        placeholder="Enter account number"
                      />
                    </FormControl>
                  </HStack>

                  <HStack spacing={4}>
                    <FormControl>
                      <FormLabel>Bank Name</FormLabel>
                      <Input
                        name="bank_name"
                        value={formData.payment_details.bank_name}
                        onChange={handlePaymentChange}
                        placeholder="Enter bank name"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>IFSC Code</FormLabel>
                      <Input
                        name="ifsc_code"
                        value={formData.payment_details.ifsc_code}
                        onChange={handlePaymentChange}
                        placeholder="Enter IFSC code"
                      />
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <FormLabel>UPI ID</FormLabel>
                    <Input
                      name="upi_id"
                      value={formData.payment_details.upi_id}
                      onChange={handlePaymentChange}
                      placeholder="Enter UPI ID"
                    />
                  </FormControl>
              </VStack>
            </form>
          ) : (
            // Non-doctor users (existing form)
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email"
                  type="email"
                />
              </FormControl>

              <FormControl>
                <FormLabel>New Password (optional)</FormLabel>
                <InputGroup>
                  <Input
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter new password"
                    type={showPassword ? "text" : "password"}
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>

              <FormControl>
                <FormLabel>WhatsApp Number</FormLabel>
                <Input
                  name="whatsapp_no"
                  value={formData.whatsapp_no}
                  onChange={handleChange}
                  placeholder="Enter WhatsApp number"
                  type="tel"
                />
              </FormControl>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            {isDoctor ? 'Update Profile' : 'Save Changes'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProfileSettingsModal;